import { sortBy, sortedIndexBy } from 'lodash';
import { assertExhaustive } from '@fullstory/typeutils';

/**
 * A callback used to define a parametric subfunction of a
 * {@link PiecewiseFunction}. You can provide one of these callbacks to
 * {@link PiecewiseFunction#add} instead of a constant value.
 * {@link PiecewiseFunction#at} will invoke the callback as needed with the
 * following parameters:
 *
 * You usually won't need to use the 'domainStart' and 'domainEnd' paramters,
 * but {@link PiecewiseFunction} uses them internally to make sure that you get
 * the same 'alpha' value at the same position even if your subfunction gets
 * partially overwritten and sliced into pieces. If 'alpha' didn't use the
 * original domain, overwriting half of your subfunction's domain would squeeze
 * your whole subfunction into half the space instead of just leaving half of
 * your subfunction like you'd expect.
 *
 * @param alpha - The position in the subfunction's domain that we want the
 * value for, represented as a number in [0, 1]. Usually this is the only
 * parameter you need; the others are for special situations.
 * @param pos - The absolute position in the overall function's domain that we
 * want the value for, represented as a number in [-∞, +∞].
 * @param domainStart - The position in the overall function's domain at which
 * this subfunction's domain starts. This is the 'start' value that was passed
 * to {@link PiecewiseFunction#add}.
 * @param domainStart - The position in the overall function's domain at which
 * this subfunction's domain ends. This is the 'end' value that was passed
 * to {@link PiecewiseFunction#add}.
 */
export type ParametricFunctionCallback<Value> =
  (alpha: number, pos: number, domainStart: number, domainEnd: number) => Value;
export type ParametricFunction<Value> =
  ParametricFunctionCallback<Value> |
  { at: ParametricFunctionCallback<Value> };

type Subfunction<Value> = {
  kind: 'constant';
  value: Value | undefined;
} | {
  kind: 'parametric';
  value: ParametricFunction<Value>;
  domainStart: number;
  domainEnd: number;
};

// @VisibleForTesting
export type Piece<Value> = {
  subfunction: Subfunction<Value>;
  priority: number;
  start: number;
  end: number;
};

/**
 * An immutable view of a {@link PiecewiseFunction}.
 */
export interface ImmutablePiecewiseFunction<Value> {
  [Symbol.iterator](): IterableIterator<Piece<Value>>;
  /**
   * @returns the value of this piecewise function at the provided position, or
   * undefined if this function is not defined there.
   */
  at(pos: number): Value | undefined;

  /**
   * @returns the value of this piecewise function at the provided position, if
   * defined, or else the value at the position nearest to 'pos' in the direction
   * of -∞ where this function is defined, if it exists, or else undefined.
   */
  atOrBefore(pos: number): Value | undefined;

  /**
   * @returns the value of this piecewise function at the provided position, if
   * defined, or else the value at the position nearest to 'pos' in the direction
   * of +∞ where this function is defined, if it exists, or else undefined.
   */
  atOrAfter(pos: number): Value | undefined;
}

/**
 * A piecewise function with a domain of some subset of the real numbers and a
 * range of 'Value'. Each subfunction may be defined either as a constant or as
 * a parametric function.
 *
 * Subfunctions are defined on subdomains which are closed on the left and open
 * on the right. A subdomain of zero size has no effect on the overall function
 * (and will be silently discarded); if you want a subfunction that's only
 * defined at one point, give it a size of Number.EPSILON. Subfunctions may have
 * infinitely large domains; Number.NEGATIVE_INFINITY and
 * Number.POSITIVE_INFINITY are valid subdomain endpoints.
 *
 * {@link PiecewiseFunction}'s functionality is pretty general, but its primary
 * use case is modeling time-varying values. In that context, you can think of
 * it as a timeline.
 *
 * Often you'll want to add all of the subfunctions up front and only do lookups
 * from then on. The {@link ImmutablePiecewiseFunction} interface has been
 * provided to make it easy to use {@link PiecewiseFunction} that way.
 *
 * In addition to the normal mathematical properties of a function (a domain and
 * a definition for the function over that domain), {@link PiecewiseFunction}
 * assigns each subfunction a "priority". To understand why this is useful,
 * consider what happens when a new subfunction is added with a domain that
 * overlaps an existing subfunction. By default, the new subfunction
 * "overwrites" the overlapping portion of the domain. That's usually what you
 * want, but the downside is that the result is dependent on the order in which
 * you add the subfunctions, which can make constructing the
 * {@link PiecewiseFunction} more complex and error prone. Priorities solve this
 * problem by letting you specify which subfunction should "win".
 * Higher-priority subfunctions will "overwrite" lower-priority subfunctions
 * even if the lower-priority subfunctions are added later. It's recommended
 * that you use priorities whenever you construct complex
 * {@link PiecewiseFunctions} with overlapping subfunctions; they make things a
 * lot simpler.
 */
export class PiecewiseFunction<Value> implements ImmutablePiecewiseFunction<Value> {
  private readonly _pieces: Piece<Value>[] = [{
    subfunction: { kind: 'constant', value: undefined },
    priority: Number.NEGATIVE_INFINITY,
    start: Number.NEGATIVE_INFINITY,
    end: Number.POSITIVE_INFINITY,
  }];

  *[Symbol.iterator](): IterableIterator<Piece<Value>> {
    yield* this._pieces;
  }

  /**
   * Add a new subfunction on the subdomain [start, end).
   * @param subfunction - The new subfunction.
   * @param start - The inclusive lower bound of the new subfunction's subdomain.
   * @param end - The exclusive uppper bound of the new subfunction's subdomain.
   * @param priority - The subfunction's priority relative to other subfunctions
   * that overlap the subdomain. The value of the overall function at each point
   * is the value of the highest-priority subfunction at that point. Ties are
   * resolved in favor of the most recently added subfunction.
   */
  add(
    subfunction: ParametricFunction<Value> | Value | undefined,
    start: number,
    end: number,
    priority: number = 0,
  ): void {
    this._add(subfunction, start, end, priority);
  }

  /**
   * @returns the value of this piecewise function at the provided position, or
   * undefined if this function is not defined there.
   */
  at(pos: number): Value | undefined {
    const piece = this._pieces[this._indexFor(pos)];
    if (!piece) return undefined;
    return evaluateAt(pos, piece);
  }

  /**
   * @returns the value of this piecewise function at the provided position, if
   * defined, or else the value at the position nearest to 'pos' in the direction
   * of -∞ where this function is defined, if it exists, or else undefined.
   */
  atOrBefore(pos: number): Value | undefined {
    for (let index = this._indexFor(pos); index >= 0; index--) {
      const piece = this._pieces[index];
      if (!piece) continue;
      const value = evaluateAt(Math.min(pos, piece.end), piece);
      if (value !== undefined) { return value; }
    }
    return undefined;
  }

  /**
   * @returns the value of this piecewise function at the provided position, if
   * defined, or else the value at the position nearest to 'pos' in the direction
   * of +∞ where this function is defined, if it exists, or else undefined.
   */
  atOrAfter(pos: number): Value | undefined {
    for (let index = this._indexFor(pos); index < this._pieces.length; index++) {
      const piece = this._pieces[index];
      if (!piece) continue;
      const value = evaluateAt(Math.max(pos, piece.start), piece);
      if (value !== undefined) { return value; }
    }
    return undefined;
  }

  /**
   * @return the index of the piece containing the provided position.
   */
  private _indexFor(pos: number): number {
    // Find the index where a piece that starts at 'pos' would need to be
    // inserted to maintain sort order. If the piece at that index starts at
    // 'pos' exactly, then it's the piece we're looking for. Otherwise, 'pos' is
    // somewhere between that piece and the one before it, so the one before it
    // is the one we want.
    const desired = { start: pos } as Piece<Value>;
    const index = sortedIndexBy(this._pieces, desired, r => r.start);

    // If the index is past the end of the array, return the last index in the
    // array. The last subdomain always ends at +∞, but 'pos' may still be past
    // the starting point of that subdomain, so this can happen even though
    // nothing can actually lie past the last piece.
    if (index >= this._pieces.length) { return index - 1; }

    // Check if we got an exact match. (The '<=' comparison is just paranoia,
    // since floating point numbers are weird; we really only need '==='.)
    const atIndex = this._pieces[index]!;
    if (atIndex.start <= pos) { return index; }

    // We want the previous piece.
    return Math.max(index - 1, 0);
  }

  /**

   * The shared implementation of {@link PiecewiseFunction#add} and
   * {@link PiecewiseFunction#overwrite}. Accepts the same parameters as those
   * methods.
   * @returns the index of the first piece the new subfunction generated, or
   * undefined if no pieces were generated for the new subfunction. (Perhaps
   * because the new subfunction's domain is empty.)
   */
  private _add(
    subfunction: ParametricFunction<Value> | Value | undefined,
    start: number,
    end: number,
    priority: number = 0,
  ): number | undefined {
    if (Number.isNaN(start) || Number.isNaN(end)) {
      throw new Error(`Can't add a subdomain with a NaN endpoint.`);
    }
    if (end < start) {
      throw new Error(`Can't add a subdomain of negative size.`);
    }
    if (start === end) {
      return undefined;  // Adding an empty subdomain is OK, but has no effect.
    }

    // Represent the new subfunction as a live range. (We reuse the Piece data
    // structure for this purpose; the difference is that live ranges can
    // overlap, while pieces never do.)
    const newPiece: Piece<Value> = {
      subfunction: isParametric(subfunction) ? {
        kind: 'parametric',
        value: subfunction,
        domainStart: start,
        domainEnd: end,
      } : {
        kind: 'constant',
        value: subfunction,
      },
      start,
      end,
      priority,
    };

    // The algorithm below merges the new subfunction with the existing pieces
    // that overlap or abut its domain. We want to preserve these invariants:
    //  1. The subdomains of all pieces should be disjoint.
    //  2. The union of the subdomains of all pieces should be [-∞, +∞].
    //  3. No two contiguous pieces should have the same value and priority. (In
    //     other words, we want to merge pieces that are identical except for
    //     their subdomains.)
    //  4. At every point, the subfunction assigned to that point is the
    //     highest priority subfunction that has been assigned to that point,
    //     or the most recently assigned subfunction if there is a tie.
    //
    // The approach is as follows:
    //  1. Create a "live range" set that includes the endpoints of the existing
    //     pieces that overlap or abut the new subfunction. (We need abutting
    //     pieces so that we can merge them with the new subfunction if
    //     necessary to preserve invariant #3.) Add the new subfunction to this
    //     set as well. For convenience we use the Piece data structure to
    //     represent these ranges, but the interpretation is different: the live
    //     ranges are potentially overlapping, while the output pieces we
    //     generate never are. (They satisfy invariant #1.)
    //
    //  2. Create a sorted array of the potential transition points between
    //     subfunctions. A transition point is an endpoint of a live range.  In
    //     the subdomain between transition points, the set of "live"
    //     subfunctions - in other words, the set of subfunctions whose live
    //     range intersects that subdomain - is constant, so we can choose a
    //     single "best" subfunction that covers that entire subdomain. ("Best"
    //     is chosen to satisfy invariant #4.)
    //
    //  3. Iterate over the transition points, pick the best subfunction
    //     between each transition point and the previous one, and generate an
    //     output piece. If the output piece has the same value and priority as
    //     the previous output piece, we merge it with the previous one. (This
    //     is to satisfy invariant #3.)
    //
    //  4. Replace the existing pieces that overlap or abut the new subfunction
    //     with the new pieces we generated in the previous step.

    // Start by finding the existing pieces that overlap or abut the new
    // subfunction (and hence need to be rebuilt) and generate the set of live
    // ranges and transition points. This implements step 1 and 2 above.
    const {
      liveRanges,
      rebuildStartIndex,
      rebuildEndIndex,
      transitionPoints,
    } = findRebuildIndicesAndLiveRanges(this._pieces, newPiece);

    // Bail early if the entire range of pieces to be rebuilt has a higher
    // priority than the new subfunction. We know that nothing will change.
    if (liveRanges.size === 0) {
      return undefined;
    }

    let newPieceRelativeStartIndex: number | undefined;
    let previousPiece: Piece<Value> | undefined;
    const toInsert: Piece<Value>[] = [];
    for (const transitionPos of transitionPoints) {
      // Find the highest priority live range that's defined on the domain
      // between the previous transition point and this one.
      let bestLiveRange: Piece<Value> | undefined;
      for (const liveRange of liveRanges) {
        if (liveRange.end <= transitionPos) {
          liveRanges.delete(liveRange);  // This range is now dead.
          continue;
        }

        if (liveRange.start > transitionPos) {
          continue;  // This range hasn't started yet.
        }

        if (!bestLiveRange) {
          bestLiveRange = liveRange;
        } else if (bestLiveRange.priority < liveRange.priority) {
          bestLiveRange = liveRange;
        } else if (
          bestLiveRange.priority === liveRange.priority &&
          liveRange === newPiece
        ) {
          bestLiveRange = liveRange;
        }
      }

      // Mark the previous piece we generated as ending here. This is how we
      // merge pieces; instead of placing a piece with the same subfunction as
      // the previous one, we skip that piece and expand the previous one to
      // fill the gap.
      if (previousPiece) {
        previousPiece.end = transitionPos;
      }

      if (!bestLiveRange) {
        // All live ranges are now dead. This means we've reached the last
        // transition point, which represents the point just past the sequence
        // of pieces that we're rebuilding, so our job is complete.
        break;
      }

      if (!!previousPiece && adjacentPiecesAreMergeable(previousPiece, bestLiveRange)) {
        // Skip this range; it's redundant with the previous one.
      } else {
        const nextPiece: Piece<Value> = {
          subfunction: bestLiveRange.subfunction,
          priority: bestLiveRange.priority,
          start: transitionPos,
          end: bestLiveRange.end,
        };

        // Record the position within 'toInsert' of the first piece we generate
        // for the new subfunction. (Because of priorities, it's hard to tell
        // where it'll end up being.)
        if (
          bestLiveRange === newPiece &&
          newPieceRelativeStartIndex === undefined
        ) {
          newPieceRelativeStartIndex = toInsert.length;
        }

        previousPiece = nextPiece;
        toInsert.push(nextPiece);
      }
    }

    if (toInsert.length < 1) {
      console.warn(`Inserted subfunction didn't generate any new pieces?`);
    }

    // Remove the old pieces and insert the new ones.
    const toRemove = rebuildEndIndex - rebuildStartIndex;
    this._pieces.splice(rebuildStartIndex, toRemove, ...toInsert);

    return newPieceRelativeStartIndex === undefined
      ? undefined
      : rebuildStartIndex + newPieceRelativeStartIndex;
  }

  // @VisibleForTesting
  _getPieces(): readonly Piece<Value>[] { return this._pieces; }
}

function isParametric<Value>(
  subfunction: ParametricFunction<Value> | Value | undefined,
): subfunction is ParametricFunction<Value> {
  if (!subfunction) { return false; }
  // Note: The Array prototype defines an at() function!
  if (Array.isArray(subfunction)) { return false; }
  if (typeof subfunction === 'function') { return true; }
  if (typeof subfunction !== 'object') { return false; }
  if (typeof (subfunction as any).at === 'function') { return true; }
  return false;
}

// @VisibleForTesting
export function adjacentPiecesAreMergeable<Value>(
  a: Piece<Value>,
  b: Piece<Value>,
): boolean {
  if (a.priority !== b.priority) { return false; }
  if (a.subfunction === b.subfunction) { return true; }
  if (a.subfunction.kind !== b.subfunction.kind) { return false; }
  if (a.subfunction.value !== b.subfunction.value) { return false; }
  if (
    a.subfunction.kind === 'parametric' &&
    b.subfunction.kind === 'parametric' &&
    (
      a.subfunction.domainStart !== b.subfunction.domainStart ||
      a.subfunction.domainEnd !== b.subfunction.domainEnd
    )
  ) {
    return false;
  }
  return true;
}

function evaluateAt<Value>(pos: number, piece: Piece<Value>): Value | undefined {
  const subfunction = piece.subfunction;
  switch (subfunction.kind) {
    case 'constant':
      return subfunction.value;

    case 'parametric': {
      const domainSize = subfunction.domainEnd - subfunction.domainStart;
      const alpha = (pos - subfunction.domainStart) / domainSize;
      const value = subfunction.value;
      return typeof value === 'function'
        ? value(alpha, pos, subfunction.domainStart, subfunction.domainEnd)
        : value.at(alpha, pos, subfunction.domainStart, subfunction.domainEnd);
    }
    default:
      assertExhaustive(subfunction, `Unexpected subfunction kind`);
  }
}

/**
 * Given a new live range that we'd like to merge into the provided set of
 * pieces, calculate:
 *   1. The indices of the existing pieces that overlap or abut the new live
 *      range. These are the pieces we'll need to rebuild.
 *   2. A set of live ranges that includes those existing pieces as well as
 *      the new live range.
 *   3. A sorted array of potential transition points between live ranges.
 *      Every endpoint of a live range is a potential transition point.
 *
 * See the comments in {@link PiecewiseFunction#_add} for more context about
 * how this information is used.
 *
 * @VisibleForTesting
 */
export function findRebuildIndicesAndLiveRanges<Value>(
  pieces: Piece<Value>[],
  newLiveRange: Piece<Value>,
) {
  // Find the first piece that overlaps or abuts the new live range.
  const rebuildStartIndex =
    Math.max(sortedIndexBy(pieces, newLiveRange, r => r.start) - 1, 0);

  const liveRanges = new Set<Piece<Value>>();
  const transitionPoints = new Set<number>();

  // Walk over the existing pieces until we find the last one that overlaps or
  // abuts the new live range, stopping early if all of the remaining pieces
  // have a higher priority than the new live range. Update the set of live
  // ranges and transition points as we go.
  let rebuiltPieceCount = 0;
  let nextFeasiblePiece: number | undefined = rebuildStartIndex - 1;
  let lastLiveRangeEnd = Number.NEGATIVE_INFINITY;
  for (let i: number = rebuildStartIndex; i < pieces.length; i++) {
    // The next feasible piece is the next piece that has a low enough priority
    // that we might need to rebuild it. If we're past the previous one we
    // found, scan forward to make sure there's another one coming up.
    if (nextFeasiblePiece < i) {
      nextFeasiblePiece = undefined;
      for (let j: number = i; j < pieces.length; j++) {
        if (pieces[j]!.priority <= newLiveRange.priority) {
          nextFeasiblePiece = j;
          break;
        }
        if (pieces[j]!.start >= newLiveRange.end) {
          break;
        }
      }
    }

    // If there are no more feasible pieces, we can stop here. Although we
    // *could* rebuild the remaining pieces that intersect the new live range,
    // there's no point, because they're all higher priority and won't get
    // changed.
    if (nextFeasiblePiece === undefined) {
      break;
    }

    const piece = pieces[i]!;
    lastLiveRangeEnd = piece.end;
    liveRanges.add(piece);
    rebuiltPieceCount++;
    transitionPoints.add(piece.start);
    transitionPoints.add(piece.end);

    if (piece.start >= newLiveRange.end) { break; }
  }

  // If we didn't find any pieces to rebuild, bail early and return an empty set
  // of live ranges. This is a signal to the caller that we don't need to do any
  // further work.
  if (liveRanges.size === 0) {
    return {
      liveRanges,
      rebuildStartIndex,
      rebuildEndIndex: rebuildStartIndex,
      transitionPoints: [],
    };
  }

  // Add the new live range itself to the set of live ranges. Note that there's
  // a subtle point here: if we decided to stop the loop above early because the
  // remaining pieces all had a higher priority than the new live range, its
  // right endpoint may lie past the end of the sequence of pieces we're
  // rebuilding. We discard this endpoint from the set of transition points so
  // it doesn't confuse the rebuilding code.
  liveRanges.add(newLiveRange);
  transitionPoints.add(newLiveRange.start);
  if (newLiveRange.end < lastLiveRangeEnd) {
    transitionPoints.add(newLiveRange.end);
  }

  return {
    liveRanges,
    rebuildStartIndex,
    rebuildEndIndex: rebuildStartIndex + rebuiltPieceCount,
    transitionPoints: sortBy([...transitionPoints]),
  };
}
